import { ItemTile } from '@/components/auctions/item-tile';
import { Layout } from '@/components/layouts/layout';
import { Button } from '@/components/ui/button';
import {
    useAuctionWithItems,
    useReorderAuctionItems,
    useUpdateAuction,
    useUpdateAuctionItem,
} from '@/hooks/use-auctions';
import {
    formatAuctionMoney,
    getRefPriceCents,
    type AuctionItem,
} from '@/types/auctions';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
    ArrowRight,
    Check,
    ChevronDown,
    ChevronUp,
    Copy,
    RefreshCw,
    Search,
    SkipForward,
    X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/auctions/$auctionId/live')({
    component: LivePage,
});

function LivePage() {
    const { auctionId } = Route.useParams();
    const navigate = useNavigate();
    const { data, isLoading } = useAuctionWithItems(auctionId);
    const updateItem = useUpdateAuctionItem(auctionId);
    const reorder = useReorderAuctionItems(auctionId);
    const updateAuction = useUpdateAuction(auctionId);

    const [liveView, setLiveView] = useState<'focus' | 'overview'>('focus');
    const [liveIndex, setLiveIndex] = useState(0);
    const [showQueueEnd, setShowQueueEnd] = useState(false);
    const [showEndConfirm, setShowEndConfirm] = useState(false);
    const [liveQuery, setLiveQuery] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const items = data?.items ?? [];

    // Initialize liveIndex to first queued item
    useEffect(() => {
        if (items.length === 0) return;
        const firstQueued = items.findIndex((i) => i.status === 'queued');
        if (firstQueued !== -1) setLiveIndex(firstQueued);
    }, [items, items.length]);

    const counts = useMemo(() => {
        let sold = 0,
            noSale = 0,
            skipped = 0,
            queued = 0,
            revenue = 0;
        items.forEach((i) => {
            if (i.status === 'sold') {
                sold++;
                revenue += i.sale_price_cents ?? 0;
            } else if (i.status === 'no_sale') noSale++;
            else if (i.status === 'skipped') skipped++;
            else queued++;
        });
        return { sold, noSale, skipped, queued, revenue };
    }, [items]);

    function findNextQueued(from: number): number | null {
        for (let k = from + 1; k < items.length; k++) {
            if (items[k].status === 'queued') return k;
        }
        for (let k = 0; k < from; k++) {
            if (items[k].status === 'queued') return k;
        }
        return null;
    }

    function advance(afterIndex: number) {
        const next = findNextQueued(afterIndex);
        if (next !== null) {
            setLiveIndex(next);
        } else {
            setShowQueueEnd(true);
        }
    }

    function markSold() {
        if (!items[liveIndex]) return;
        const id = items[liveIndex].id;
        const soldCount = items.filter((i) => i.status === 'sold').length;
        updateItem.mutate(
            {
                id,
                input: {
                    status: 'sold',
                    sold_at: new Date().toISOString(),
                    sold_order: soldCount + 1,
                },
            },
            { onError: () => toast.error('Failed to update') },
        );
        advance(liveIndex);
    }

    function markNoSale() {
        if (!items[liveIndex]) return;
        const id = items[liveIndex].id;
        updateItem.mutate(
            { id, input: { status: 'no_sale' } },
            { onError: () => toast.error('Failed to update') },
        );
        advance(liveIndex);
    }

    function markSkip() {
        if (!items[liveIndex]) return;
        const id = items[liveIndex].id;
        updateItem.mutate(
            { id, input: { status: 'skipped' } },
            { onError: () => toast.error('Failed to update') },
        );
        advance(liveIndex);
    }

    function copyNameSize() {
        const item = items[liveIndex];
        if (!item?.inventory_items) return;
        const inv = item.inventory_items;
        const parts = [inv.description ?? ''];
        if (inv.size) parts.push(`Size ${inv.size}`);
        if (inv.weight_oz != null) parts.push(`${inv.weight_oz} oz`);
        navigator.clipboard?.writeText(parts.join(' · ')).catch(() => {});
        toast.success(`Copied "${inv.description}"`);
    }

    function handleJump(idx: number) {
        setLiveIndex(idx);
        setLiveView('focus');
    }

    function handleReorder(id: string, direction: 'up' | 'down') {
        const idx = items.findIndex((i) => i.id === id);
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= items.length) return;
        reorder.mutate([
            { id: items[idx].id, position: items[swapIdx].position },
            { id: items[swapIdx].id, position: items[idx].position },
        ]);
        if (liveIndex === idx) setLiveIndex(swapIdx);
        else if (liveIndex === swapIdx) setLiveIndex(idx);
    }

    function handleRestartUnsold() {
        const unsold = items.filter(
            (i) => i.status === 'skipped' || i.status === 'no_sale',
        );
        unsold.forEach((item) => {
            updateItem.mutate({ id: item.id, input: { status: 'queued' } });
        });
        const firstUnsold = items.findIndex(
            (i) => i.status === 'skipped' || i.status === 'no_sale',
        );
        if (firstUnsold !== -1) setLiveIndex(firstUnsold);
        setShowQueueEnd(false);
        toast.success(`${unsold.length} item(s) requeued`);
    }

    function handleConfirmEnd() {
        updateAuction.mutate(
            { status: 'completed', completed_at: new Date().toISOString() },
            {
                onSuccess: () =>
                    navigate({
                        to: '/auctions/$auctionId/review',
                        params: { auctionId },
                    }),
            },
        );
    }

    const current = items[liveIndex] ?? null;
    const nextQueuedIdx = (() => {
        for (let k = liveIndex + 1; k < items.length; k++)
            if (items[k].status === 'queued') return k;
        return -1;
    })();

    const filteredGrid = liveQuery
        ? items.filter((i) => {
              const q = liveQuery.toLowerCase();
              return (
                  i.inventory_items?.description?.toLowerCase().includes(q) ||
                  i.inventory_items?.brand?.toLowerCase().includes(q)
              );
          })
        : items;

    const hasUnsold = counts.skipped + counts.noSale > 0;
    const unresolvedCount = counts.queued + counts.skipped;

    if (isLoading) {
        return (
            <Layout>
                <div className="max-w-6xl mx-auto px-4 py-8">
                    <div className="h-8 w-48 rounded animate-pulse bg-[#111726]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
                {/* Live header */}
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <div className="flex items-center gap-2.75 mb-2">
                            <span className="flex items-center gap-1.5 text-[11.5px] font-bold tracking-[.5px] px-2.75 py-1.25 rounded-xl text-[#f0556b] bg-[rgba(240,85,107,.12)] border border-[rgba(240,85,107,.3)]">
                                <span className="w-1.75 h-1.75 rounded-full bg-[#f0556b] animate-pulse" />
                                LIVE
                            </span>
                            <h1 className="text-[28px] font-bold tracking-tight [font-family:var(--font-heading)] text-[#f5f7fb]">
                                {data?.title}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2.25 text-[14px] font-mono">
                            <span className="text-[#34d27f]">
                                {counts.sold} sold
                            </span>
                            <span className="text-[#3a4258]">·</span>
                            {counts.skipped > 0 && (
                                <>
                                    <span className="text-[#f5a524]">
                                        {counts.skipped} skipped
                                    </span>
                                    <span className="text-[#3a4258]">·</span>
                                </>
                            )}
                            <span className="text-[#9aa2c0]">
                                {counts.noSale} passed
                            </span>
                            <span className="text-[#3a4258]">·</span>
                            <span className="text-[#7ba6f5]">
                                {counts.queued} left
                            </span>
                            {counts.revenue > 0 && (
                                <>
                                    <span className="text-[#3a4258]">·</span>
                                    <span className="text-[#34d27f]">
                                        {formatAuctionMoney(counts.revenue)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Focus/Overview toggle */}
                        <div className="flex gap-0.5 p-0.5 rounded-[10px] bg-[#0c111d] border border-[#1b2235]">
                            {(['focus', 'overview'] as const).map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setLiveView(v)}
                                    className={`px-4 py-2 rounded-[7px] text-[13px] font-semibold cursor-pointer capitalize ${liveView === v ? 'bg-primary text-white' : 'bg-transparent text-[#9aa2c0]'}`}
                                >
                                    {v.charAt(0).toUpperCase() + v.slice(1)}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowEndConfirm(true)}
                            className="text-[13.5px] font-bold text-white px-4.5 py-2.5 rounded-[10px] cursor-pointer bg-[#f0556b] hover:bg-[#e04060]"
                        >
                            End Auction
                        </button>
                    </div>
                </div>

                {/* Main two-column layout */}
                <div className="grid grid-cols-[1fr_392px] gap-4.5 items-start">
                    {/* Left: Focus or Overview */}
                    <div>
                        {liveView === 'focus' && current && (
                            <FocusCard
                                item={current}
                                posLabel={`#${liveIndex + 1} of ${items.length}`}
                                onSold={markSold}
                                onNoSale={markNoSale}
                                onSkip={markSkip}
                                onCopy={copyNameSize}
                                isPending={updateItem.isPending}
                            />
                        )}

                        {liveView === 'overview' && (
                            <div>
                                <div className="flex items-center gap-2.25 h-10.5 px-3.5 rounded-[11px] mb-3.5 bg-[#0c111d] border border-[#1d2536]">
                                    <Search
                                        size={16}
                                        className="text-[#6b7390] shrink-0"
                                    />
                                    <input
                                        value={liveQuery}
                                        onChange={(e) =>
                                            setLiveQuery(e.target.value)
                                        }
                                        placeholder="Search this show..."
                                        className="flex-1 bg-transparent border-none outline-none text-[14px] text-[#f5f7fb]"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {filteredGrid.map((item) => {
                                        const origIdx = items.indexOf(item);
                                        const isCurrent = origIdx === liveIndex;
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() =>
                                                    handleJump(origIdx)
                                                }
                                                className="cursor-pointer rounded-[14px] overflow-hidden text-left bg-[#0e1422] border border-[#1b2235]"
                                            >
                                                <div className="relative w-full aspect-square">
                                                    <ItemTile
                                                        item={
                                                            item.inventory_items
                                                        }
                                                        borderRadius={0}
                                                    />
                                                    {item.status === 'sold' && (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[rgba(52,210,127,.4)]">
                                                            <Check
                                                                size={28}
                                                                color="#fff"
                                                            />
                                                        </div>
                                                    )}
                                                    {item.status ===
                                                        'skipped' && (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[rgba(245,165,36,.25)] text-[#f5a524]">
                                                            <SkipForward
                                                                size={24}
                                                            />
                                                            <span className="text-[10px] font-bold tracking-[.4px]">
                                                                SKIPPED
                                                            </span>
                                                        </div>
                                                    )}
                                                    {item.status ===
                                                        'no_sale' && (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[rgba(4,6,11,.66)] text-[#9aa2c0]">
                                                            <X size={28} />
                                                            <span className="text-[10px] font-bold tracking-[.4px]">
                                                                NO SALE
                                                            </span>
                                                        </div>
                                                    )}
                                                    {isCurrent && (
                                                        <>
                                                            <div className="absolute inset-0 border-[2.5px]  border-primary-300" />
                                                            <div className="absolute top-2 left-2 text-[9px] font-extrabold tracking-[.5px] text-white px-2 py-0.75 rounded-[6px] bg-primary-500">
                                                                NOW
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="px-2.75 pt-2.25 pb-2.75">
                                                    <div className="text-[12.5px] font-semibold truncate text-[#e7eaf3]">
                                                        {item.inventory_items
                                                            ?.description ??
                                                            '—'}
                                                    </div>
                                                    <div className="text-[11px] mt-0.5 text-[#6b7390]">
                                                        {
                                                            item.inventory_items
                                                                ?.brand
                                                        }
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: Queue feed */}
                    <div className="rounded-2xl overflow-hidden bg-[#111726] border border-[#232b3f] sticky top-19.5">
                        <div className="flex items-center justify-between px-4 py-3.75 border-b border-b-[#1a2235]">
                            <span className="font-semibold text-[15px] [font-family:var(--font-heading)] text-[#f5f7fb]">
                                Queue feed
                            </span>
                            <span className="text-[11px] text-[#6b7390]">
                                click to jump · arrows reorder
                            </span>
                        </div>
                        <div className="p-2 flex flex-col gap-1.25 max-h-145 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#222a3d_transparent]">
                            {items.map((item, idx) => {
                                const isCurrent = idx === liveIndex;
                                const isNext = idx === nextQueuedIdx;
                                const resolved =
                                    item.status === 'sold' ||
                                    item.status === 'no_sale';
                                let badge = {
                                    label: 'Queued',
                                    bg: '#141b2c',
                                    fg: '#6b7390',
                                };
                                if (isCurrent)
                                    badge = {
                                        label: 'NOW',
                                        bg: ' rgba(108,74,240,.18)',
                                        fg: '#9d86ff',
                                    };
                                else if (item.status === 'sold')
                                    badge = {
                                        label: formatAuctionMoney(
                                            item.sale_price_cents,
                                        ),
                                        bg: 'rgba(52,210,127,.12)',
                                        fg: '#34d27f',
                                    };
                                else if (item.status === 'skipped')
                                    badge = {
                                        label: 'Skipped',
                                        bg: 'rgba(245,165,36,.13)',
                                        fg: '#f5a524',
                                    };
                                else if (item.status === 'no_sale')
                                    badge = {
                                        label: 'No sale',
                                        bg: 'rgba(148,163,184,.1)',
                                        fg: '#9aa2c0',
                                    };
                                else if (isNext)
                                    badge = {
                                        label: 'Up next',
                                        bg: 'rgba(91,141,239,.13)',
                                        fg: '#7ba6f5',
                                    };

                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => handleJump(idx)}
                                        className={`flex items-center gap-2.25 px-2.25 py-2 rounded-[11px] cursor-pointer border ${isCurrent ? 'bg-primary-400/10 border-primary-800/80' : 'bg-[#0d1322] border-[#1a2235]'}`}
                                    >
                                        <span className="text-[10.5px] font-bold text-center shrink-0 w-4 font-mono text-[#5d667e]">
                                            {idx + 1}
                                        </span>
                                        <div
                                            className={`w-9.5 h-9.5 shrink-0 rounded-[9px] overflow-hidden ${resolved && !isCurrent ? 'opacity-60' : 'opacity-100'}`}
                                        >
                                            <ItemTile
                                                item={item.inventory_items}
                                                borderRadius={9}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className={`text-[12.5px] font-semibold truncate ${resolved ? 'text-[#9aa2c0]' : 'text-[#e7eaf3]'}`}
                                            >
                                                {item.inventory_items
                                                    ?.description ?? '—'}
                                            </div>
                                            <div className="text-[10.5px] truncate text-[#6b7390]">
                                                {item.inventory_items?.brand}
                                            </div>
                                        </div>
                                        <span
                                            className="text-[10.5px] font-bold px-2 py-1 rounded-[7px] shrink-0 whitespace-nowrap"
                                            style={{
                                                color: badge.fg,
                                                background: badge.bg,
                                            }}
                                        >
                                            {badge.label}
                                        </span>
                                        <div
                                            className="flex flex-col shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={() =>
                                                    handleReorder(item.id, 'up')
                                                }
                                                disabled={idx === 0}
                                                className="cursor-pointer disabled:opacity-20 text-[#54607a] leading-[0.6]"
                                            >
                                                <ChevronUp size={13} />
                                            </button>
                                            <button
                                                onClick={() =>
                                                    handleReorder(
                                                        item.id,
                                                        'down',
                                                    )
                                                }
                                                disabled={
                                                    idx === items.length - 1
                                                }
                                                className="cursor-pointer disabled:opacity-20 text-[#54607a] leading-[0.6]"
                                            >
                                                <ChevronDown size={13} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* End-of-queue modal */}
                {showQueueEnd && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 bg-[rgba(4,6,11,.72)] backdrop-blur-[3px]">
                        <div className="w-115 rounded-[18px] p-7 text-center bg-[#121829] border border-[#232b3f]">
                            <div className="w-16.5 h-16.5 rounded-full flex items-center justify-center mx-auto mb-4.5 bg-[rgba(108,74,240,.14)]">
                                <svg
                                    width={28}
                                    height={28}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#9d86ff"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M4 21V4M4 4h13l-2 4 2 4H4" />
                                </svg>
                            </div>
                            <h2 className="font-bold text-[23px] mb-2 [font-family:var(--font-heading)] text-[#f5f7fb]">
                                You've reached the end of the queue
                            </h2>
                            <p className="text-[13.5px] leading-[1.55] mb-1.5 text-[#9aa2c0]">
                                {counts.sold} sold so far this show.
                            </p>
                            {hasUnsold ? (
                                <p className="text-[13.5px] leading-[1.55] mb-5.5 text-[#9aa2c0]">
                                    {counts.skipped + counts.noSale} item(s)
                                    were skipped or didn't sell — run them
                                    again, or wrap up.
                                </p>
                            ) : (
                                <p className="text-[13.5px] leading-[1.55] mb-5.5 text-[#9aa2c0]">
                                    Everything's been actioned. Nice work.
                                </p>
                            )}
                            <div className="flex flex-col gap-2.5">
                                {hasUnsold && (
                                    <button
                                        onClick={handleRestartUnsold}
                                        className="flex items-center justify-center gap-2.25 px-4 py-3.5 rounded-[12px] text-[14.5px] font-bold text-white cursor-pointer bg-primary-500 hover:bg-primary-500"
                                    >
                                        <RefreshCw size={16} color="#fff" />
                                        Restart queue of unsold items
                                    </button>
                                )}
                                <button
                                    onClick={handleConfirmEnd}
                                    className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-[12px] text-[14.5px] font-bold cursor-pointer border ${hasUnsold ? 'text-[#cdd3e6] bg-[#141b2c] border-[#2a3450]' : 'text-white  bg-primary-500 border-primary-500 hover:bg-primary-400'}`}
                                >
                                    End auction &amp; review{' '}
                                    <ArrowRight size={15} />
                                </button>
                                {hasUnsold && (
                                    <button
                                        onClick={() => setShowQueueEnd(false)}
                                        className="py-2.75 text-[13px] font-semibold cursor-pointer text-[#9aa2c0]"
                                    >
                                        Keep browsing the queue
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* End confirm modal */}
                {showEndConfirm && (
                    <div
                        className="fixed inset-0 flex items-center justify-center z-50 bg-[rgba(4,6,11,.72)] backdrop-blur-[3px]"
                        onClick={() => setShowEndConfirm(false)}
                    >
                        <div
                            className="w-110 rounded-[18px] p-6.5 bg-[#121829] border border-[#232b3f]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="font-bold text-[21px] mb-2 [font-family:var(--font-heading)] text-[#f5f7fb]">
                                End this auction?
                            </h2>
                            <p className="text-[13.5px] leading-normal mb-5.5 text-[#9aa2c0]">
                                {unresolvedCount} item(s) are still unresolved.
                                You can mark them on the Review screen before
                                syncing.
                            </p>
                            <div className="flex gap-2.75 justify-end">
                                <button
                                    onClick={() => setShowEndConfirm(false)}
                                    className="px-4.5 py-3 rounded-[11px] text-[14px] font-semibold cursor-pointer text-[#cdd3e6] bg-[#141b2c] hover:bg-[#1e2840]"
                                >
                                    Keep going
                                </button>
                                <button
                                    onClick={handleConfirmEnd}
                                    disabled={updateAuction.isPending}
                                    className="px-5 py-3 rounded-[11px] text-[14px] font-bold text-white cursor-pointer disabled:opacity-60 bg-[#f0556b] hover:bg-[#e04060]"
                                >
                                    End &amp; review
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

interface FocusCardProps {
    item: AuctionItem;
    posLabel: string;
    onSold: () => void;
    onNoSale: () => void;
    onSkip: () => void;
    onCopy: () => void;
    isPending: boolean;
}

function FocusCard({
    item,
    posLabel,
    onSold,
    onNoSale,
    onSkip,
    onCopy,
    isPending,
}: FocusCardProps) {
    const inv = item.inventory_items;
    const refPriceCents = getRefPriceCents(inv);
    const isSold = item.status === 'sold';
    const isNoSale = item.status === 'no_sale';
    const isSkipped = item.status === 'skipped';

    return (
        <div className="rounded-[18px] p-5.5 grid grid-cols-[320px_1fr] gap-6 min-h-95 bg-[#111726] border border-[#2c2350]">
            {/* Photo */}
            <div className="relative rounded-2xl overflow-hidden w-[320px] h-80 shadow-[0_18px_40px_-18px_rgba(0,0,0,.7)]">
                <ItemTile item={inv} borderRadius={16} />
                <div className="absolute top-3 left-3 text-[11px] font-bold tracking-[.4px] text-white px-2.5 py-1.25 rounded-xl whitespace-nowrap bg-[rgba(4,6,11,.55)] backdrop-blur-xs">
                    {posLabel}
                </div>
                {isSkipped && (
                    <div className="absolute top-3 right-3 text-[10.5px] font-bold tracking-[.4px] px-2.25 py-1 rounded-xl text-[#f5a524] bg-[rgba(245,165,36,.15)] border border-[rgba(245,165,36,.35)]">
                        SKIPPED EARLIER
                    </div>
                )}
            </div>

            {/* Info + actions */}
            <div className="flex flex-col">
                <div className="text-[11px] font-bold tracking-[.6px] mb-2 text-[#9d86ff]">
                    NOW SHOWING
                </div>
                <h2 className="font-semibold text-[28px] leading-[1.12] mb-3 [font-family:var(--font-heading)] text-[#f5f7fb]">
                    {inv?.description ?? '—'}
                </h2>
                <div className="flex items-center flex-wrap gap-2.5 text-[14px] mb-4.5 text-[#9aa2c0]">
                    <span className="text-[#c3c9de] font-semibold">
                        {inv?.brand}
                    </span>
                    {inv?.size && (
                        <>
                            <span className="text-[#3a4258]">·</span>
                            <span>Size {inv.size}</span>
                        </>
                    )}
                    {inv?.weight_oz != null && (
                        <>
                            <span className="text-[#3a4258]">·</span>
                            <span>{inv.weight_oz} oz</span>
                        </>
                    )}
                    {inv?.condition && (
                        <>
                            <span className="text-[#3a4258]">·</span>
                            <span>{inv.condition}</span>
                        </>
                    )}
                </div>

                {/* Listed price + Copy button */}
                <div className="flex items-center flex-wrap gap-2.5 mb-auto">
                    {refPriceCents != null && (
                        <div className="flex items-center gap-2 text-[13px] px-3.5 py-2.25 rounded-[10px] bg-[#0d1322] border border-[#232b3f]">
                            <span className="text-[#9aa2c0]">Listed</span>
                            <span className="font-bold text-[15px] font-mono text-[#9d86ff]">
                                {formatAuctionMoney(refPriceCents)}
                            </span>
                        </div>
                    )}
                    <button
                        onClick={onCopy}
                        className="flex items-center gap-1.5 text-[13px] font-semibold px-3.5 py-2.25 rounded-[10px] cursor-pointer transition-colors text-[#cdd3e6] bg-[#0d1322] border border-[#232b3f] hover:border-[#3a4565] hover:text-white"
                    >
                        <Copy size={15} /> Copy name &amp; size
                    </button>
                </div>

                {/* Status pill */}
                {(isSold || isNoSale || isSkipped) && (
                    <div
                        className={`flex items-center gap-2 mt-5.5 -mb-1 text-[12.5px] font-semibold ${isSold ? 'text-[#34d27f]' : isSkipped ? 'text-[#f5a524]' : 'text-[#9aa2c0]'}`}
                    >
                        <span
                            className={`w-2 h-2 rounded-full shrink-0 ${isSold ? 'bg-[#34d27f]' : isSkipped ? 'bg-[#f5a524]' : 'bg-[#9aa2c0]'}`}
                        />
                        {isSold
                            ? 'Marked sold — add price & buyer after the show'
                            : isNoSale
                              ? 'Marked not sold'
                              : 'Skipped — loops back at end of queue'}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                    <Button
                        className={`${isSold ? 'bg-[rgba(52,210,127,.24)]' : 'bg-[rgba(52,210,127,.1)]'} hover:bg-[rgba(52,210,127,.22)] ${isSold ? 'border-[#34d27f]' : 'border-[rgba(52,210,127,.4)]'} border text-[#34d27f] uppercase cursor-pointer font-bold disabled:opacity-60 transition-colors`}
                        onClick={onSold}
                        disabled={isPending}
                        size="lg"
                    >
                        <Check size={18} color="#34d27f" strokeWidth={2.5} />{' '}
                        Sold
                    </Button>

                    <Button
                        onClick={onNoSale}
                        disabled={isPending}
                        size="lg"
                        className={`${isNoSale ? 'bg-[#1e273c]' : 'bg-[#10172680]'} border ${isNoSale ? 'border-[#54607a]' : 'border-[#2a3450]'} uppercase cursor-pointer font-bold disabled:opacity-60 transition-colors`}
                    >
                        <X size={18} color="#9aa2c0" strokeWidth={2.2} />
                        Not Sold
                    </Button>

                    <Button
                        onClick={onSkip}
                        disabled={isPending}
                        className={`${isSkipped ? 'bg-[rgba(245,165,36,.24)]' : 'bg-[rgba(245,165,36,.1)]'} border ${isSkipped ? 'border-[#f5a524]' : 'border-[rgba(245,165,36,.4)]'} uppercase cursor-pointer font-bold disabled:opacity-60 transition-colors text-[#f5a524]`}
                    >
                        <SkipForward size={16} color="#f5a524" fill="#f5a524" />{' '}
                        Skip
                    </Button>
                </div>
                <p className="text-center text-[11.5px] mt-2.75 text-[#5d667e]">
                    Price &amp; buyer get added after the show — keep it quick.
                </p>
            </div>
        </div>
    );
}
